/**
 * Разметка в сообщениях: **жирный**, *курсив*, `код`, списки, ссылки.
 *
 * Готовый парсер сюда не берём: в комментариях пишут обычный текст, и целая
 * библиотека Markdown ради четырёх приёмов — лишний вес и лишняя поверхность
 * для ошибок. Разбор строит React-узлы, а не HTML-строку, поэтому вставить
 * разметку через комментарий невозможно по устройству.
 */
import type { ReactNode } from 'react'
import './RichText.css'

/** Ссылки распознаём только http(s): mailto и прочее в комментариях не нужны. */
const LINK = /(https?:\/\/[^\s<>()]+[^\s<>().,;:!?])/g
/** Порядок важен: тройные звёздочки разбираются до двойных, двойные до одинарных. */
const INLINE = /(`[^`\n]+`|\*\*\*[^*\n]+\*\*\*|\*\*[^*\n]+\*\*|\*[^*\n]+\*|_[^_\n]+_)/g

function withLinks(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = []
  let index = 0
  for (const part of text.split(LINK)) {
    if (part === '') continue
    if (LINK.test(part)) {
      LINK.lastIndex = 0
      out.push(
        <a key={`${keyPrefix}l${index}`} href={part} target="_blank" rel="noopener noreferrer">{part}</a>,
      )
    } else {
      out.push(part)
    }
    index += 1
  }
  return out
}

function inline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = []
  let index = 0
  for (const part of text.split(INLINE)) {
    if (part === '') continue
    const key = `${keyPrefix}i${index}`
    index += 1
    if (part.startsWith('`') && part.endsWith('`') && part.length > 2) {
      out.push(<code key={key}>{part.slice(1, -1)}</code>)
    } else if (part.startsWith('***') && part.endsWith('***') && part.length > 6) {
      out.push(<strong key={key}><em>{part.slice(3, -3)}</em></strong>)
    } else if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      out.push(<strong key={key}>{part.slice(2, -2)}</strong>)
    } else if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
      out.push(<em key={key}>{part.slice(1, -1)}</em>)
    } else if (part.startsWith('_') && part.endsWith('_') && part.length > 2) {
      out.push(<em key={key}>{part.slice(1, -1)}</em>)
    } else {
      out.push(...withLinks(part, key))
    }
  }
  return out
}

export interface RichTextProps {
  text: string
  className?: string
}

/** Текст сообщения с разметкой. Абзацы — пустой строкой, списки — «- » или «1. ». */
export function RichText({ text, className }: RichTextProps) {
  const lines = text.split('\n')
  const blocks: ReactNode[] = []
  let list: { ordered: boolean; items: string[] } | null = null

  const flushList = (key: string): void => {
    if (!list) return
    const items = list.items.map((item, i) => <li key={`${key}li${i}`}>{inline(item, `${key}${i}`)}</li>)
    blocks.push(list.ordered
      ? <ol key={key} className="rich__list">{items}</ol>
      : <ul key={key} className="rich__list">{items}</ul>)
    list = null
  }

  lines.forEach((raw, index) => {
    const line = raw.trimEnd()
    const bullet = /^\s*[-•*]\s+(.*)$/.exec(line)
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line)

    if (bullet) {
      if (!list || list.ordered) { flushList(`l${index}`); list = { ordered: false, items: [] } }
      list.items.push(bullet[1] ?? '')
      return
    }
    if (numbered) {
      if (!list || !list.ordered) { flushList(`l${index}`); list = { ordered: true, items: [] } }
      list.items.push(numbered[1] ?? '')
      return
    }

    flushList(`l${index}`)
    if (line.trim() === '') return
    blocks.push(<p key={`p${index}`} className="rich__p">{inline(line, `p${index}`)}</p>)
  })
  flushList('lend')

  return <div className={className ? `rich ${className}` : 'rich'}>{blocks}</div>
}

/**
 * Обрамление выделенного куска парой символов — для Cmd+B и соседей.
 * Возвращает новый текст и границы выделения после правки, чтобы курсор
 * остался на том же месте текста, а не прыгнул в конец.
 */
export function wrapSelection(
  value: string, start: number, end: number, marker: string,
): { value: string; start: number; end: number } {
  const selected = value.slice(start, end)
  const before = value.slice(0, start)
  const after = value.slice(end)

  /* Повторное нажатие снимает обрамление — так ведут себя все редакторы. */
  if (before.endsWith(marker) && after.startsWith(marker)) {
    return {
      value: before.slice(0, -marker.length) + selected + after.slice(marker.length),
      start: start - marker.length,
      end: end - marker.length,
    }
  }
  if (selected.startsWith(marker) && selected.endsWith(marker) && selected.length > marker.length * 2) {
    const inner = selected.slice(marker.length, -marker.length)
    return { value: before + inner + after, start, end: start + inner.length }
  }

  return {
    value: `${before}${marker}${selected}${marker}${after}`,
    start: start + marker.length,
    end: end + marker.length,
  }
}
